import type {
  NekoseiBirthplace,
  NekoseiBirthSetting,
  NekoseiContent,
  NekoseiPatternId,
} from "@/domains/nekosei/NekoseiContent";

/** 0以上1未満の値を返す乱数。テストでは固定列を渡す */
export type NekoseiRandom = () => number;

export type NekoseiSex = "female" | "male";

export type NekoseiCoat =
  "brownTabby" | "grayTabby" | "calico" | "black" | "white" | "tuxedo";

export type NekoseiDwelling = "house" | "apartment";

export interface NekoseiHousehold {
  /** 迎えられたときの飼い主の年齢 */
  humanAge: number;
  partnerAge: number;
  dwelling: NekoseiDwelling;
  /** マンションなら何階か */
  floor: number;
  rooms: number;
  residentCat: boolean;
  partnerFromSeason?: number;
  childFromSeason?: number;
  workFromHomeFromSeason?: number;
  newKittenFromSeason?: number;
  moveFromSeason?: number;
  dwellingAfterMove: NekoseiDwelling;
  roomsAfterMove: number;
  surgerySeason?: number;
  /** 飼い主が入院する季節 */
  hospitalSeason?: number;
  businessTripSeason?: number;
  /** 飼い主が仕事を辞め、転職するまでの一連の出来事が始まる季節 */
  jobChangeFromSeason?: number;
  /** 地震や台風で停電する季節 */
  disasterSeason?: number;
  /** 飼い主の親が同居し始める季節 */
  grandparentFromSeason?: number;
  retirementFromSeason?: number;
  /** 保護団体の子猫を一時的に預かり始める季節 */
  fosterKittenFromSeason?: number;
  renovationSeason?: number;
}

export interface NekoseiProfile {
  /** 名前は迎えられたときに決まる。それまでは画面に出さない */
  name: string;
  sex: NekoseiSex;
  coat: NekoseiCoat;
  longHair: boolean;
  birthplace: NekoseiBirthplace;
  setting: NekoseiBirthSetting;
  spot: string;
  birthText: string;
  /** 生まれた月（3〜5月） */
  birthMonth: number;
  littersize: number;
  birthOrder: number;
  motherCoat: NekoseiCoat;
  pattern: NekoseiPatternId;
  childSex: NekoseiSex;
  household: NekoseiHousehold;
  /** 健康に影響する選択をする前の寿命（季節の数） */
  baseLifespanSeasons: number;
  /** 大人になったときの、ちょうどいい体重（kg） */
  idealWeightKg: number;
  /** 出生届の番号 */
  certificateNumber: number;
}

export const SEASONS_PER_YEAR = 4;

export const NEKOSEI_COAT_LABELS: Record<NekoseiCoat, string> = {
  brownTabby: "茶トラ",
  grayTabby: "サバトラ",
  calico: "三毛",
  black: "黒",
  white: "白",
  tuxedo: "ハチワレ",
};

export const NEKOSEI_PATTERN_LABELS: Record<NekoseiPatternId, string> = {
  master: "主人",
  bigCat: "大きな猫",
  staff: "ごはん係",
  roommate: "同居人",
  retainer: "家来",
  ourHuman: "うちの人間",
};

export const NEKOSEI_BIRTHPLACE_LABELS: Record<NekoseiBirthplace, string> = {
  outside: "外",
  home: "飼い猫の家",
  petShop: "ペットショップ・ブリーダー",
  shelter: "保護団体",
};

const CAT_NAMES = [
  "むぎ",
  "こむぎ",
  "きなこ",
  "もも",
  "ソラ",
  "レオ",
  "ルナ",
  "ミケ",
  "タマ",
  "クウ",
  "あずき",
  "おもち",
  "ココ",
  "マロン",
  "ハナ",
  "トラ吉",
  "ちゃちゃ",
  "ノア",
  "うに",
  "ごま",
] as const;

const PATTERN_IDS: readonly NekoseiPatternId[] = [
  "master",
  "bigCat",
  "staff",
  "roommate",
  "retainer",
  "ourHuman",
];

const COATS: readonly NekoseiCoat[] = [
  "brownTabby",
  "grayTabby",
  "calico",
  "black",
  "white",
  "tuxedo",
];

// 出典: 一般社団法人ペットフード協会「令和7年（2025年）全国犬猫飼育実態調査」
// 「ペット入手時の情報源・入手先」猫 ペットの入手先 Q27(2)（複数回答、単位%）
// https://petfood.or.jp/pdf/data/2025/9.pdf
// 1年以内飼育開始者（n=143）は標本が少ないため、3年以内飼育開始者（n=328）の値を使う。
// 調査は「入手先」なので、入手先ごとに猫がどこで生まれたかを想定して生まれる場所に対応づける。
// 友人・知人の家の猫が産んだ子を譲り受けた場合を想定し、友人・知人・親族からの入手は飼い猫の家とする。
// 「その他」（1.2%）は対応づけられないため除く。
const CAT_ACQUISITION_SHARES: readonly {
  acquisition: string;
  birthplace: NekoseiBirthplace;
  percent: number;
}[] = [
  { acquisition: "ペットショップで購入", birthplace: "petShop", percent: 26.8 },
  {
    acquisition: "業者のブリーダーから直接購入",
    birthplace: "petShop",
    percent: 10.4,
  },
  {
    acquisition: "友人／知人のブリーダーから直接購入",
    birthplace: "petShop",
    percent: 3.4,
  },
  {
    acquisition: "インターネットのペット販売を通じて購入",
    birthplace: "petShop",
    percent: 1.8,
  },
  {
    acquisition: "シェルターからの有償譲渡・購入",
    birthplace: "shelter",
    percent: 2.1,
  },
  {
    acquisition: "シェルターからの無償譲渡",
    birthplace: "shelter",
    percent: 3.0,
  },
  {
    acquisition: "里親探しのマッチングサイトからの譲渡・購入",
    birthplace: "shelter",
    percent: 5.2,
  },
  {
    acquisition: "里親探しのマッチングサイトからの無償譲渡",
    birthplace: "shelter",
    percent: 4.3,
  },
  {
    acquisition: "譲渡会を通じての無償譲渡",
    birthplace: "shelter",
    percent: 7.6,
  },
  {
    acquisition: "友人／知人／親族からの譲渡・購入",
    birthplace: "home",
    percent: 8.5,
  },
  {
    acquisition: "友人／知人／親族からの無償譲渡",
    birthplace: "home",
    percent: 12.8,
  },
  {
    acquisition: "飼育している犬・猫が産んだ",
    birthplace: "home",
    percent: 2.7,
  },
  { acquisition: "野良犬・猫を拾った", birthplace: "outside", percent: 15.5 },
];

const BIRTHPLACE_WEIGHTS: readonly (readonly [NekoseiBirthplace, number])[] =
  CAT_ACQUISITION_SHARES.map(({ birthplace, percent }) => [
    birthplace,
    percent,
  ]);

/** 生まれる場所ごとの、入手先の割合（%）の合計。出生届に出す */
export const nekoseiBirthplaceShare = (birthplace: NekoseiBirthplace): number =>
  CAT_ACQUISITION_SHARES.filter((share) => share.birthplace === birthplace)
    .map((share) => share.percent)
    .reduce((sum, percent) => sum + percent, 0);

// 出典: 一般社団法人ペットフード協会「令和7年（2025年）全国犬猫飼育実態調査」
// 「主要指標サマリー」犬猫 平均寿命の推移
// https://petfood.or.jp/pdf/data/2025/3.pdf
// 猫全体16.00歳（外に出ない猫16.23歳、外に出る猫15.12歳）。
// 外に出るかどうかは猫生の途中の選択で決まるため、生まれたときは全体の値を使う。
export const NEKOSEI_AVERAGE_LIFESPAN_YEARS = 16.0;

// 一般社団法人ペットフード協会「令和7年（2025年）全国犬猫飼育実態調査」
// 「主要指標サマリー」猫 総飼育頭数（拡大推計）8,847千頭
// https://petfood.or.jp/pdf/data/2025/3.pdf
export const NEKOSEI_PET_CAT_COUNT = 8_847_000;
// 調査は平均しか公表していないため、ばらつきの幅はゲーム上の仮定。
const LIFESPAN_SPREAD_YEARS = 6.5;

export const pick = <T>(items: readonly T[], random: NekoseiRandom): T =>
  items[Math.floor(random() * items.length)];

export const pickWeighted = <T>(
  entries: readonly (readonly [T, number])[],
  random: NekoseiRandom,
): T => {
  const total = entries.reduce((sum, [, weight]) => sum + weight, 0);
  let threshold = random() * total;
  for (const [value, weight] of entries) {
    threshold -= weight;
    if (threshold < 0) return value;
  }
  return entries[entries.length - 1][0];
};

export const randomInt = (min: number, max: number, random: NekoseiRandom) =>
  min + Math.floor(random() * (max - min + 1));

const sampleLifespanSeasons = (random: NekoseiRandom): number => {
  const centered = (random() + random() + random()) / 3 - 0.5;
  return Math.round(
    (NEKOSEI_AVERAGE_LIFESPAN_YEARS + 2 * LIFESPAN_SPREAD_YEARS * centered) *
      SEASONS_PER_YEAR,
  );
};

// 家庭の出来事が起きる確率や時期は、ゲーム上の設定
/** 定年退職の年齢になる季節。猫を迎えたときにすでに過ぎていれば退職しない */
const retirementSeason = (
  humanAge: number,
  random: NekoseiRandom,
): number | undefined => {
  const retirementAge = randomInt(60, 65, random);
  return humanAge < retirementAge
    ? (retirementAge - humanAge) * SEASONS_PER_YEAR + 1
    : undefined;
};

const sometimes = (
  probability: number,
  from: number,
  to: number,
  random: NekoseiRandom,
): number | undefined =>
  random() < probability ? randomInt(from, to, random) : undefined;

const createHousehold = (random: NekoseiRandom): NekoseiHousehold => {
  const partnerFromSeason =
    random() < 0.55 ? randomInt(4, 24, random) : undefined;
  const childFromSeason =
    partnerFromSeason !== undefined && random() < 0.55
      ? partnerFromSeason + randomInt(6, 16, random)
      : undefined;
  const dwelling: NekoseiDwelling = random() < 0.5 ? "apartment" : "house";
  const humanAge = randomInt(22, 48, random);
  return {
    humanAge,
    partnerAge: randomInt(22, 48, random),
    dwelling,
    floor: randomInt(2, 12, random),
    rooms:
      dwelling === "house" ? randomInt(4, 6, random) : randomInt(1, 3, random),
    residentCat: random() < 0.2,
    partnerFromSeason,
    childFromSeason,
    workFromHomeFromSeason:
      random() < 0.4 ? randomInt(4, 40, random) : undefined,
    newKittenFromSeason:
      random() < 0.25 ? randomInt(12, 36, random) : undefined,
    moveFromSeason: random() < 0.35 ? randomInt(8, 40, random) : undefined,
    dwellingAfterMove: random() < 0.5 ? "apartment" : "house",
    roomsAfterMove: randomInt(2, 5, random),
    surgerySeason: random() < 0.9 ? 2 : undefined,
    hospitalSeason: sometimes(0.2, 8, 48, random),
    businessTripSeason: sometimes(0.3, 6, 48, random),
    jobChangeFromSeason: sometimes(0.2, 6, 40, random),
    disasterSeason: sometimes(0.25, 4, 48, random),
    grandparentFromSeason:
      humanAge >= 35 ? sometimes(0.3, 8, 36, random) : undefined,
    retirementFromSeason: retirementSeason(humanAge, random),
    fosterKittenFromSeason: sometimes(0.15, 8, 40, random),
    renovationSeason: sometimes(0.2, 8, 48, random),
  };
};

export const createNekoseiProfile = (
  content: NekoseiContent,
  random: NekoseiRandom,
): NekoseiProfile => {
  const birthplace = pickWeighted(BIRTHPLACE_WEIGHTS, random);
  const birth = pick(content.birthSettings[birthplace], random);
  const coat = pick(COATS, random);
  // 三毛猫はほぼメス
  const sex: NekoseiSex =
    coat === "calico" || random() < 0.5 ? "female" : "male";
  const longHair = random() < 0.15;
  const littersize = randomInt(3, 6, random);
  return {
    name: pick(CAT_NAMES, random),
    sex,
    coat,
    longHair,
    birthplace,
    setting: birth.setting,
    spot: birth.spot,
    birthText: birth.text,
    birthMonth: randomInt(3, 5, random),
    littersize,
    // すぐ上の兄（クロ）がいるため、2番目以降に生まれる
    birthOrder: randomInt(2, littersize, random),
    motherCoat: pick(COATS, random),
    pattern: pick(PATTERN_IDS, random),
    childSex: random() < 0.5 ? "female" : "male",
    household: createHousehold(random),
    baseLifespanSeasons: sampleLifespanSeasons(random),
    // 体重の目安はゲーム上の設定（オス4.8kg・メス3.9kg前後、長毛は少し重め）
    idealWeightKg:
      Math.round(
        ((sex === "male" ? 4.8 : 3.9) +
          (longHair ? 0.3 : 0) +
          (random() - 0.5) * 0.8) *
          10,
      ) / 10,
    certificateNumber: randomInt(0, 999999, random),
  };
};
